package org.gymly.ui.admin

import org.gymly.model.AdminMembershipItem
import org.gymly.model.AdminMembershipStatus
import org.gymly.model.AdminScheduleSlot
import org.gymly.model.AdminWorkDaySchedule

internal fun normalizeHhMm(raw: String): String? {
    val t = raw.trim()
    val match = Regex("""^(\d{1,2}):(\d{2})$""").matchEntire(t) ?: return null
    val h = match.groupValues[1].toIntOrNull() ?: return null
    val m = match.groupValues[2].toIntOrNull() ?: return null
    if (h !in 0..23 || m !in 0..59) return null
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
}

internal fun minutesOf(time: String): Int {
    val parts = time.take(5).split(":")
    return (parts.getOrNull(0)?.toIntOrNull() ?: 0) * 60 + (parts.getOrNull(1)?.toIntOrNull() ?: 0)
}

internal fun formatMinutes(total: Int): String {
    val h = ((total / 60) % 24).coerceAtLeast(0)
    val m = total % 60
    return "${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}"
}

/**
 * Filas de 1 hora a partir del horario de apertura de la empresa
 * (unión de todos los bloques, p. ej. 06–12 y 14–21 → 06–07, …, 11–12, 14–15, …).
 */
internal fun hourlyRowsFromCompanyHours(operatingHours: List<AdminWorkDaySchedule>): List<String> {
    val ranges = linkedSetOf<IntRange>()
    for (day in operatingHours) {
        for (block in day.blocks) {
            val start = normalizeHhMm(block.startTime.take(5)) ?: continue
            val end = normalizeHhMm(block.endTime.take(5)) ?: continue
            var cursor = minutesOf(start)
            val endMin = minutesOf(end)
            if (endMin <= cursor) continue
            while (cursor + 60 <= endMin) {
                ranges.add(cursor until (cursor + 60))
                cursor += 60
            }
            if (cursor < endMin) {
                ranges.add(cursor until endMin)
            }
        }
    }
    return ranges
        .sortedBy { it.first }
        .map { "${formatMinutes(it.first)}-${formatMinutes(it.last + 1)}" }
}

/** Fallback: parte bloques de horarios existentes en franjas de 1 h. */
internal fun hourlyRowsFromSchedules(schedules: List<AdminScheduleSlot>): List<String> {
    val ranges = linkedSetOf<IntRange>()
    for (slot in schedules) {
        val start = normalizeHhMm(slot.startTime.take(5)) ?: continue
        val end = normalizeHhMm(slot.endTime.take(5)) ?: continue
        var cursor = minutesOf(start)
        val endMin = minutesOf(end)
        if (endMin <= cursor) continue
        while (cursor + 60 <= endMin) {
            ranges.add(cursor until (cursor + 60))
            cursor += 60
        }
        if (cursor < endMin) {
            ranges.add(cursor until endMin)
        }
    }
    return ranges
        .sortedBy { it.first }
        .map { "${formatMinutes(it.first)}-${formatMinutes(it.last + 1)}" }
}

internal fun parseHourRow(timeKey: String): Pair<Int, Int>? {
    val parts = timeKey.split('-')
    if (parts.size != 2) return null
    val start = normalizeHhMm(parts[0]) ?: return null
    val end = normalizeHhMm(parts[1]) ?: return null
    return minutesOf(start) to minutesOf(end)
}

internal fun slotOverlapsHourRow(slot: AdminScheduleSlot, timeKey: String): Boolean {
    val row = parseHourRow(timeKey) ?: return false
    val start = minutesOf(slot.startTime)
    val end = minutesOf(slot.endTime)
    return start < row.second && end > row.first
}

/**
 * Personas con membresía activa (plan diario, semanal o mensual) asignadas al entrenador
 * y vigentes en el día indicado.
 */
internal fun enrolledCountForTrainerOnDay(
    memberships: List<AdminMembershipItem>,
    trainerId: String?,
    dayIso: String?,
): Int {
    if (trainerId.isNullOrBlank() || dayIso.isNullOrBlank()) return 0
    val day = dayIso.take(10)
    return memberships
        .asSequence()
        .filter { it.status == AdminMembershipStatus.ACTIVE }
        .filter { it.trainerId == trainerId }
        .filter { it.startsAt.take(10) <= day }
        .filter { it.endsAt == null || it.endsAt.take(10) >= day }
        .map { it.clientId }
        .distinct()
        .count()
}

/** Mapa (trainerId|dayIso) → cantidad de inscritos, para reutilizar en la grilla. */
internal fun enrolledCountsByTrainerDay(
    memberships: List<AdminMembershipItem>,
    trainerIds: Collection<String>,
    dayIsos: Collection<String>,
): Map<String, Int> {
    val days = dayIsos.map { it.take(10) }.filter { it.length >= 10 }.distinct()
    val trainers = trainerIds.filter { it.isNotBlank() }.distinct()
    if (days.isEmpty() || trainers.isEmpty()) return emptyMap()
    val active = memberships.filter { it.status == AdminMembershipStatus.ACTIVE && it.trainerId != null }
    return buildMap {
        for (trainerId in trainers) {
            for (day in days) {
                val n = active
                    .asSequence()
                    .filter { it.trainerId == trainerId }
                    .filter { it.startsAt.take(10) <= day }
                    .filter { it.endsAt == null || it.endsAt.take(10) >= day }
                    .map { it.clientId }
                    .distinct()
                    .count()
                put("$trainerId|$day", n)
            }
        }
    }
}

internal fun enrolledCountKey(trainerId: String?, dayIso: String?): String? {
    if (trainerId.isNullOrBlank() || dayIso.isNullOrBlank()) return null
    return "$trainerId|${dayIso.take(10)}"
}
